import { FormValues } from "./types";
import { Parser } from "expr-eval";
import { formatNumber } from "../applications/gravity-form/components/calculator/helpers";
import { ltrim } from "../util-string";

const mergeTagRegexpArray = [
    // regex from the old template function filled with empty groups to match other regexes
    /\{()()(?!input_)([\d_a-zA-Z\-.]+)()(?::([a-z]+))?\}/g,
    /\{((.*?):(\d+)\.?(\d+)?)(?::([a-z]+))?\}/g,
    /\{((\w*)input_(\d+)\.?(\d+)?)(?::([a-z]+))?\}/g
];

/**
 * Replaces a string with the value of the merge tag from the context, which can be any type
 * @param {string} template
 * @param {FormValues} values
 * @returns {any}
 */
const replaceMergeTags = (
    template: string,
    values: FormValues
): any => {
    let returnValue = template;
    mergeTagRegexpArray.forEach((regex, index) => {
        Array.from(template.matchAll(regex)).forEach((match: RegExpMatchArray) => {
            returnValue = values[index === 0 ? match[3] : `input_${match[3]}`];
        });
    });
    return returnValue;
}

/**
 * Replaces merge tags in string with the input values from context
 * @param {string} template
 * @param {FormValues} values
 * @param {boolean} calculation
 *
 * @example
 * // returns "Hello, World" (if input with id of 10 has the value "World")
 * replaceMergeTagsInString("Hello, {:10}"", FormValues);
 *
 * @returns {string}
 */
 const replaceMergeTagsInString = (
    template: string,
    values: FormValues,
    calculation?: boolean
) => {
    mergeTagRegexpArray.forEach((regex, index) => {
        Array.from(template.matchAll(regex)).forEach((match: RegExpMatchArray) => {
            const inputValue = getInputValueFromContext(
                match[3],
                match[4],
                match[5],
                values,
                index === 0
            );
            template = template.replace(
                match[0],
                calculation && inputValue === "" ? "0" : inputValue
            );
        });
    });

    return template;
};

/**
 * Replaces smart punctuation characters with normal punctuation characters
 * @param {string} value 
 * @returns {string}
 */
const replacePunctuationCharacters = (value: string): string =>
    value
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, "\"");

/**
 * Returns the value or other property of the input from the context
 * @param {string} inputId
 * @param {string} inputPart
 * @param {string} modifier
 * @param {FormValues} values
 * @param {boolean} isUnique
 * @returns {string}
 */
const getInputValueFromContext = (
    inputId: string,
    inputPart = "",
    modifier = "",
    values: FormValues,
    isUnique: boolean
) => {
    const inputKey = isUnique ? inputId : `input_${inputId}`;
    const inputValue = values[inputKey] || "";
    let returnValue = "";
    switch (modifier) {
        case "currency": {
            const formattedNumber =
                formatNumber(getStringValue(inputValue, inputId, inputPart));
            returnValue = !isNaN(formattedNumber[1]) ? formattedNumber[0] : "";
            break;
        }
        case "label":
            returnValue = (inputValue || {}).label || inputValue;
            break;
        default:
            returnValue = getStringValue(inputValue, inputId, inputPart);
    }
    return returnValue || "";
};

const getStringValue = (
    inputValue: any,
    inputId: string,
    inputPart: string
): string => {
    if (inputValue && typeof inputValue === "object") {
        // dropdown field's value
        if (inputValue["value"]) {
            return inputValue["value"];
        } else {
            // multiselect field's values
            if (typeof inputValue[0] === "object") {
                const returnValueArray: string[] = [];
                inputValue.forEach((value: any) => {
                    returnValueArray.push(value["value"]);
                });
                return returnValueArray.join("; ");
            } else {
                // multi-input field's value
                return typeof inputValue[inputId + "." + inputPart] === "object"
                    ? inputValue[inputId + "." + inputPart]["value"]
                    : inputValue[inputId + "." + inputPart];
            }
        }
    } else {
        // single input field's value
        return inputValue;
    }
}

const stringHasValueTag = (stringToTest: string) =>
    mergeTagRegexpArray[0].test(stringToTest)
    || mergeTagRegexpArray[1].test(stringToTest)
    || mergeTagRegexpArray[2].test(stringToTest);

const evaluateValues = (
    template: string,
    values: FormValues
): string => {
    const expr = replaceMergeTagsInString(template, values, true);
    try {
        const val = Parser.evaluate(expr);
        return isNaN(val) ? "0" : val.toString();
    } catch (error) {
        return "0";
    }
};

const evaluate = (expr: string): string => {
    try {
        const val = Parser.evaluate(expr);
        return isNaN(val) ? "0" : val.toString();
    } catch (error) {
        return "0";
    }
};

const ID = function() {
    // Math.random should be unique because of its seeding algorithm.
    // Convert it to base 36 (numbers + letters), and grab the first 9 characters
    // after the decimal.
    return (
        "_" +
        Math.random()
            .toString(36)
            .substr(2, 9)
    );
};

/**
 * Parse a date modifier rule from GravityForm date fields
 * @param {string} rule
 * @param {boolean} absolute
 * @param {object} alias
 *
 * @example
 * // returns { days: 1, months: 0, years: 0 }
 * parseDateModifier('d:1')
 * @example
 * // returns { days: 1, months: -1, years: 100 }
 * parseDateModifier('m:-1, d:1, y:100')
 * @example
 * // returns { days: 1, months: 1, years: 100 }
 * parseDateModifier('d:-1, m:1, y:1', true)
 * @example
 * // returns { foo: 1, bar: 1, baz: 1 }
 * parseDateModifier('d:1,m:1,y:1', false, { d: 'foo', m: 'bar', y: 'baz' })
 *
 * @returns {object}
 */
const parseDateModifier = (
    rule: string,
    absolute = false,
    alias: { d: string; m: string; y: string } = {
        d: "days",
        m: "months",
        y: "years"
    }
) =>
    rule
        /**
         * Reverse the order so that duplicates are overwritten by first instances
         * 'd1,d:2,m:1'
         *      -> ['d:1', 'd:2', 'm:1']
         *      -> ['m:1', 'd:2', 'm:1']
         *      -> { days: 1, months: 1, years: 0 }
         */
        .split(",")
        .reverse()
        .reduce(
            (a, b) =>
                Array.from(b.matchAll(/(?:([dmy])\s*:)\s*([+-]?\d+)/g)).reduce(
                    (c, d) => ({
                        ...c,
                        ...{
                            [alias[`${d[1]}` as "d" | "m" | "y"]]:
                                absolute
                                    ? Math.abs(parseInt(`${d[2]}`))
                                    : parseInt(`${d[2]}`)
                        }
                    }),
                    a
                ),
            Object.values(alias).reduce((o, c) => ({ ...o, ...{ [c]: 0 } }), {})
        );

/**
 * Parse a date format from GravityForm date fields
 * @param {string} formatType
 *
 * @example
 * // returns "dd/MM/yyyy"
 * parseDateFormat("dmy")
 * @example
 * // returns "MM/dd/yyyy"
 * parseDateFormat("")
 *
 * @returns {string}
 */
const parseDateFormat = (formatType: string): string => {
    return (
        {
            "dmy": "dd/MM/yyyy",
            "dmy_dash": "dd-MM-yyyy",
            "dmy_dot": "dd.MM.yyyy",
            "mdy": "MM/dd/yyyy",
            "ymd_dash": "yyyy-MM-dd",
            "ymd_dot": "yyyy.MM.dd",
            "ymd_slash": "yyyy/MM/dd"
        } as { [key: string]: any }
    )[formatType] || parseDateFormat("mdy");
};

const internationalPhoneNumber = (code: string, phone: string) =>
    `+${code}${ltrim(ltrim(ltrim(phone, "\\+"), code), "0")}`;

const isValidUrl = (url: string) => {
    try {
        new URL(url);
        return true;
    } catch (_) {
        return false;
    }
};

/**
 * Returns with a reject promise after the specified time
 * @param {number} milliseconds
 * @param {string} message
 * 
 * @returns {Promise<never>}
 */
const timedReject = (milliseconds: number, message: string): Promise<never> => {
    return new Promise((_resolve, reject) => {
        setTimeout(() => {
            reject(message);
        }, milliseconds);
    });
};

export {
    ID,
    replaceMergeTags,
    replaceMergeTagsInString,
    evaluateValues,
    evaluate,
    stringHasValueTag,
    parseDateModifier,
    parseDateFormat,
    replacePunctuationCharacters,
    internationalPhoneNumber,
    isValidUrl,
    timedReject
};
